import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Renders the standalone static marketing site (plain HTML/CSS/JS, served from
 * /public/landing) inside a full-viewport iframe.
 *
 * Links that should leave the marketing site and enter this app (e.g. the
 * "Explore Learning" CTA -> /home) use target="_top" in the HTML. As a
 * safety net we also intercept same-origin clicks to /home or /courses here
 * and route them client-side, avoiding a full page reload.
 */
const APP_PATHS = ["/home", "/courses"];

export default function LandingPage() {
  const iframeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const attach = () => {
      let doc;
      try {
        doc = iframe.contentDocument || iframe.contentWindow?.document;
      } catch (e) {
        return; // cross-origin – nothing we can do
      }
      if (!doc) return;

      const onClick = (e) => {
        const anchor = e.target.closest && e.target.closest("a");
        if (!anchor) return;
        let pathname;
        try {
          pathname = new URL(anchor.href, doc.baseURI).pathname;
        } catch (err) {
          return;
        }
        if (APP_PATHS.includes(pathname)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          navigate(pathname);
        }
      };

      doc.addEventListener("click", onClick, true);
      iframe._detach = () => doc.removeEventListener("click", onClick, true);
    };

    iframe.addEventListener("load", attach);
    attach(); // in case it is already loaded

    return () => {
      iframe.removeEventListener("load", attach);
      if (iframe._detach) iframe._detach();
    };
  }, [navigate]);

  return (
    <iframe
      ref={iframeRef}
      title="Upgrade Skills"
      src="/landing/index.html"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        border: "none",
      }}
    />
  );
}
